import React from 'react';
import { Link } from 'react-router-dom';

const OrderDetails = ({ orderProducts, orderInfo, onClose }) => {
  // Extract order information
  const {
    orderID,
    totalOrderAmount,
    totalDeliveryFees,
    totalPaymentAmount,
    shippingAddress,
    paymentDetails,
    deliveryScheduleDate,
    status,
    paymentConfirmation,
    orderDate
  } = orderInfo || {};

  // Enhanced status mapping with modern colors and icons
  const getStatusInfo = (statusText, paymentConfirmationStatus) => {
    if (paymentConfirmationStatus !== 'Confirmed' && !statusText) {
      return { 
        label: 'Awaiting Payment', 
        color: 'bg-gradient-to-r from-red-50 to-red-100 text-red-700 border-red-200', 
        icon: '💳',
        ring: 'ring-2 ring-red-200'
      };
    }

    // Map based on status text from API
    const normalizedStatus = statusText?.toLowerCase() || '';
    
    if (normalizedStatus.includes('payment confirmed')) {
      return { 
        label: 'Payment Confirmed', 
        color: 'bg-gradient-to-r from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200', 
        icon: '✅',
        ring: 'ring-2 ring-emerald-200'
      };
    }
    
    if (normalizedStatus.includes('processing') || normalizedStatus.includes('being processed')) {
      return { 
        label: 'Processing', 
        color: 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border-blue-200', 
        icon: '⚡',
        ring: 'ring-2 ring-blue-200'
      };
    }
    
    if (normalizedStatus.includes('pending confirmation') || normalizedStatus.includes('awaiting confirmation')) {
      return { 
        label: 'Pending Confirmation', 
        color: 'bg-gradient-to-r from-amber-50 to-amber-100 text-amber-700 border-amber-200', 
        icon: '⏳',
        ring: 'ring-2 ring-amber-200'
      };
    }
    
    if (normalizedStatus.includes('delivered') || normalizedStatus.includes('completed')) {
      return { 
        label: 'Delivered', 
        color: 'bg-gradient-to-r from-green-50 to-green-100 text-green-700 border-green-200', 
        icon: '🎉',
        ring: 'ring-2 ring-green-200'
      };
    }
    
    if (normalizedStatus.includes('shipped') || normalizedStatus.includes('transit')) {
      return { 
        label: 'Shipped', 
        color: 'bg-gradient-to-r from-purple-50 to-purple-100 text-purple-700 border-purple-200', 
        icon: '🚚',
        ring: 'ring-2 ring-purple-200'
      };
    }
    
    if (normalizedStatus.includes('cancelled')) {
      return { 
        label: 'Cancelled', 
        color: 'bg-gradient-to-r from-gray-50 to-gray-100 text-gray-700 border-gray-200', 
        icon: '❌',
        ring: 'ring-2 ring-gray-200'
      };
    }

    // Default status
    return { 
      label: statusText || 'Processing', 
      color: 'bg-gradient-to-r from-slate-50 to-slate-100 text-slate-700 border-slate-200', 
      icon: '📦',
      ring: 'ring-2 ring-slate-200'
    };
  };

  const statusInfo = getStatusInfo(status, paymentConfirmation);

  // Safe image URL parsing
  const getImageUrl = (imageUrl) => {
    if (!imageUrl) return '/Images/placeholder.png';
    
    try {
      // Check if it's already a valid URL
      if (imageUrl.startsWith('http') || imageUrl.startsWith('/')) {
        return imageUrl;
      }
      // Try to parse as JSON if it looks like a JSON array
      if (imageUrl.startsWith('[') || imageUrl.startsWith('{')) {
        const parsed = JSON.parse(imageUrl);
        return Array.isArray(parsed) ? parsed[0] : parsed;
      }
      return imageUrl;
    } catch {
      return imageUrl; // Return original if JSON parsing fails
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-4 pb-4 overflow-y-auto">
      {/* Modern Backdrop */}
      <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-40" onClick={onClose}></div>
      
      {/* Main Content */}
      <div className="relative z-50 w-full max-w-4xl mx-auto px-4 sm:px-6">
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200/50 max-h-[90vh] overflow-hidden">
          
          {/* Enhanced Header */}
          <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-700 p-6 text-white relative overflow-hidden">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="w-full h-full" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-11.046-8.954-20-20-20v20h20z'/%3E%3C/g%3E%3C/svg%3E")`
              }}></div>
            </div>
            
            <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold mb-2">Order Details</h2>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="bg-white/20 backdrop-blur-sm px-3 py-1 rounded-lg text-sm font-semibold">
                    ID: {orderID}
                  </span>
                  <div className={`px-4 py-2 rounded-xl border backdrop-blur-sm ${statusInfo.color} ${statusInfo.ring}`}>
                    <span className="text-sm font-semibold">
                      {statusInfo.icon} {statusInfo.label}
                    </span>
                  </div>
                </div>
                {orderDate && (
                  <p className="text-blue-100 mt-2 text-sm">
                    📅 Placed on {new Date(orderDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                )}
              </div>
              
              {/* Close Button */}
              <button 
                onClick={onClose}
                className="absolute top-4 right-4 sm:relative sm:top-0 sm:right-0 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white transition-all duration-200 hover:scale-105"
                aria-label="Close order details"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Scrollable Content */}
          <div className="max-h-[calc(90vh-140px)] overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-6">>
            
            {/* Products Section */}
            <div className="bg-gradient-to-r from-gray-50 to-white p-6 rounded-xl border border-gray-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-blue-600 font-semibold">📦</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Products</h3>
              </div>
              
              <div className="space-y-4">
                {orderProducts && orderProducts.map((product, index) => (
                  <div 
                    key={product.productID} 
                    className="group bg-white p-5 border border-gray-200 rounded-xl hover:shadow-lg hover:border-blue-200 transition-all duration-300"
                  >
                    <div className="flex flex-col sm:flex-row gap-4">
                      {/* Enhanced Product Image */}
                      <div className="w-24 h-24 sm:w-28 sm:h-28 flex-shrink-0">
                        <div className="relative w-full h-full bg-gray-100 rounded-xl overflow-hidden group-hover:shadow-md transition-shadow">
                          <img
                            src={getImageUrl(product.imageUrl)}
                            alt={product.productName}
                            className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                            onError={(e) => { e.target.src = '/Images/placeholder.png'; }}
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        </div>
                      </div>

                      {/* Enhanced Product Details */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-700 transition-colors">
                          {product.productName}
                        </h4>
                        
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-3">
                          <div className="bg-blue-50 px-3 py-2 rounded-lg">
                            <p className="text-xs text-blue-600 font-medium">Quantity</p>
                            <p className="text-sm font-bold text-blue-800">{product.quantity}</p>
                          </div>
                          <div className="bg-green-50 px-3 py-2 rounded-lg">
                            <p className="text-xs text-green-600 font-medium">Unit Price</p>
                            <p className="text-sm font-bold text-green-800">Ksh {product.price?.toFixed(2)}</p>
                          </div>
                          <div className="bg-purple-50 px-3 py-2 rounded-lg col-span-2 sm:col-span-1">
                            <p className="text-xs text-purple-600 font-medium">Subtotal</p>
                            <p className="text-sm font-bold text-purple-800">Ksh {(product.price * product.quantity)?.toFixed(2)}</p>
                          </div>
                        </div>

                        {/* Enhanced Track Order Link */}
                        <Link 
                          to={`/Orders/OrderTracking?orderId=${orderID}&productId=${product.productID}`}
                          state={{ orderID, productID: product.productID }}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg hover:from-blue-600 hover:to-purple-700 transition-all duration-200 hover:shadow-lg font-medium text-sm"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                          </svg>
                          Track Order
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Enhanced Delivery Address Section */}
            {shippingAddress && (
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-blue-600">📍</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Delivery Address</h3>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Full Name</p>
                        <p className="text-sm font-semibold text-gray-900">{shippingAddress.name}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone Number</p>
                        <p className="text-sm font-semibold text-gray-900">{shippingAddress.phonenumber}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Address</p>
                        <p className="text-sm font-semibold text-gray-900">{shippingAddress.address}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Town</p>
                        <p className="text-sm font-semibold text-gray-900">{shippingAddress.town}</p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">County</p>
                        <p className="text-sm font-semibold text-gray-900">{shippingAddress.county}</p>
                      </div>
                      {shippingAddress.postalCode && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Postal Code</p>
                          <p className="text-sm font-semibold text-gray-900">{shippingAddress.postalCode}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {deliveryScheduleDate && deliveryScheduleDate !== "0001-01-01T00:00:00" && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Scheduled Delivery</p>
                      <p className="text-sm font-semibold text-blue-600">
                        {new Date(deliveryScheduleDate).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Payment Information Section */}
            {paymentDetails && paymentDetails.length > 0 && (
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                    <span className="text-green-600">💳</span>
                  </div>
                  <h3 className="text-xl font-bold text-gray-800">Payment Information</h3>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Payment Method</p>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl">📱</span>
                          <p className="text-sm font-semibold text-gray-900">{paymentDetails[0].paymentMethod}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Phone Number</p>
                        <p className="text-sm font-semibold text-gray-900">{paymentDetails[0].phonenumber}</p>
                      </div>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Payment Reference</p>
                        <p className="text-sm font-semibold text-gray-900 font-mono bg-gray-100 px-2 py-1 rounded">
                          {paymentDetails[0].paymentReference}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Payment Status</p>
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${
                          paymentConfirmation === 'Confirmed' 
                            ? 'bg-green-100 text-green-800 border border-green-200' 
                            : 'bg-yellow-100 text-yellow-800 border border-yellow-200'
                        }`}>
                          {paymentConfirmation === 'Confirmed' ? '✅' : '⏳'} 
                          {paymentConfirmation || 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>
                  {paymentDetails[0].paymentDate && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Payment Date</p>
                      <p className="text-sm font-semibold text-green-600">
                        {new Date(paymentDetails[0].paymentDate).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Enhanced Order Summary Section */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-purple-600">💰</span>
                </div>
                <h3 className="text-xl font-bold text-gray-800">Order Summary</h3>
              </div>
              
              <div className="bg-white p-5 rounded-lg shadow-sm">
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 font-medium">Items Total</span>
                    <span className="font-semibold text-gray-900">Ksh {totalOrderAmount?.toFixed(2)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 font-medium">Delivery Fee</span>
                    <span className="font-semibold text-gray-900">Ksh {totalDeliveryFees?.toFixed(2)}</span>
                  </div>
                  
                  <div className="border-t border-gray-200 pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total Amount</span>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-purple-600">Ksh {totalPaymentAmount?.toFixed(2)}</span>
                        {paymentConfirmation === 'Confirmed' && (
                          <p className="text-xs text-green-600 font-medium">✅ Payment Confirmed</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetails;